import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { AcademyOperatingProfileInput } from '@court-sync/shared';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Badge,
} from '../../components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { OperatingProfileSection } from '@/components/academy/OperatingProfileSection';
import { adminBackofficeApi } from '../../services/adminBackoffice';
import { Loader2 } from 'lucide-react';

interface UserRoleWithAcademy {
  role: string;
  academyId?: string | null;
}

interface AdminUserAcademyInsight {
  id: string;
  name: string;
  slug: string;
  subscriptionTier?: string | null;
  operatingProfile?: AcademyOperatingProfileInput | null;
  derivedCounts: {
    coaches: number;
    locations: number;
    courts: number;
    activeSports: number;
  };
}

interface AdminUserDetail {
  user: {
    id: string;
    email: string;
    displayName: string;
    createdAt?: string | Date;
    roles?: string[];
    rolesWithAcademy?: UserRoleWithAcademy[];
    primaryRole?: string;
  };
  ownedAcademies: AdminUserAcademyInsight[];
}

const ROLE_BADGE_CLASS: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  academy_owner: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  coach: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  guardian: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  student: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
};

function formatRole(role: string): string {
  return (role ?? '').replace(/_/g, ' ').toUpperCase() || '–';
}

export default function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    adminBackofficeApi
      .getUserDetail(userId)
      .then((data) => {
        if (data.success && data.data) {
          setDetail(data.data);
        } else {
          setError(data.error || 'User not found');
        }
      })
      .catch((err) => setError(err?.response?.data?.error || err?.message || 'Failed to load user'))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading && !detail) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error && !detail) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
        {error}
      </div>
    );
  }

  if (!detail) return null;

  const { user, ownedAcademies } = detail;
  const roles =
    user.roles ??
    (user.rolesWithAcademy ? [...new Set(user.rolesWithAcademy.map((r) => r.role))] : []);
  const isAcademyOwner = roles.includes('academy_owner');

  return (
    <div className="space-y-6">
      <PageHeader
        title={user.displayName}
        breadcrumbs={[{ label: 'Users', href: '/backoffice/users' }]}
        help={<p>Platform admin view of this user and their academy operating profile.</p>}
      />

      <Card>
        <CardHeader>
          <h2 className="text-lg font-medium">Account</h2>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2 text-sm">
            <div>
              <dt className="text-gray-500">Email</dt>
              <dd className="font-medium text-gray-900 dark:text-white">{user.email}</dd>
            </div>
            <div>
              <dt className="text-gray-500">User ID</dt>
              <dd className="font-mono text-xs text-gray-700 dark:text-gray-300 break-all">{user.id}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Joined</dt>
              <dd>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '–'}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Roles</dt>
              <dd className="flex flex-wrap gap-1 mt-1">
                {roles.length === 0 ? (
                  <span className="text-gray-400">–</span>
                ) : (
                  roles.map((role) => (
                    <span
                      key={role}
                      className={`px-2 py-0.5 text-xs font-semibold rounded-full ${ROLE_BADGE_CLASS[role] ?? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'}`}
                    >
                      {formatRole(role)}
                    </span>
                  ))
                )}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {isAcademyOwner ? (
        ownedAcademies.length > 0 ? (
          <div className="space-y-6">
            {ownedAcademies.map((academy) => (
              <div key={academy.id} className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">{academy.name}</h2>
                  <Badge variant="secondary">{academy.slug}</Badge>
                  {academy.subscriptionTier ? (
                    <Badge variant="outline">{academy.subscriptionTier}</Badge>
                  ) : null}
                  <Button variant="ghost" size="sm" asChild>
                    <Link to={`/backoffice/risk/${academy.id}`}>Risk profile</Link>
                  </Button>
                </div>
                <OperatingProfileSection
                  operatingProfile={academy.operatingProfile}
                  derivedCounts={academy.derivedCounts}
                  description="Declared onboarding answers vs live counts for this academy."
                />
              </div>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-8 text-sm text-muted-foreground">
              This user has the academy owner role but no owned academies were found.
            </CardContent>
          </Card>
        )
      ) : null}
    </div>
  );
}
